import * as React from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Tooltip,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import { menu, menu_main, filterStaticListByAppList } from './NavMenu'

// LAP — Sidebar permanente (remplace le menu déroulant du header, cf.
// demande Alexis « style SAP Ariba » : barre latérale fixe plutôt qu'un menu
// cliqué dans le header). Réutilise la config `menu`/`menu_main` existante de
// NavMenu.js (seule source de vérité des routes par app utilisateur) — on ne
// change ni les couleurs ni le thème des pages, on reprend la palette sombre
// déjà validée (DA SmartFeed / mobile shell : #0b0e1a / #1a1d2e / #667eea).

export const SIDEBAR_WIDTH_EXPANDED = 240
export const SIDEBAR_WIDTH_COLLAPSED = 72
const STORAGE_KEY = 'lap_sidebar_collapsed'

const colors = {
  bg: '#0b0e1a',
  paper: '#1a1d2e',
  line: 'rgba(255,255,255,0.08)',
  text: '#e0e0e0',
  text2: 'rgba(224,224,224,0.62)',
  accent: '#667eea',
  accentBg: 'rgba(102,126,234,0.16)',
  hoverBg: 'rgba(255,255,255,0.06)',
}

function readStoredCollapsed() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw === null ? null : raw === '1'
  } catch (_) {
    return null
  }
}

export default function Sidebar() {
  const { t } = useTranslation('layout')
  const theme = useTheme()
  const location = useLocation()
  const siteUser = useSelector((store) => store.user)

  // « repliable si écran large, déplié par défaut */ plié par défaut sur
  // écran large, déplié par défaut sur les autres écrans » (réponse Alexis).
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))
  const [collapsed, setCollapsed] = React.useState(() => {
    const stored = readStoredCollapsed()
    return stored !== null ? stored : isLargeScreen
  })
  const hasManualChoice = React.useRef(readStoredCollapsed() !== null)

  // Si l'utilisateur n'a jamais basculé manuellement, le défaut suit le
  // breakpoint en direct (redimensionnement de fenêtre).
  React.useEffect(() => {
    if (!hasManualChoice.current) {
      setCollapsed(isLargeScreen)
    }
  }, [isLargeScreen])

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev
      hasManualChoice.current = true
      try {
        window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
      } catch (_) {
        /* silencieux */
      }
      return next
    })
  }

  const [appMenu, setAppMenu] = React.useState([])
  React.useEffect(() => {
    const userApps = siteUser?.user?.user_apps
    setAppMenu(filterStaticListByAppList(userApps, menu))
  }, [siteUser?.user?.user_apps])

  const currentPath = location.pathname

  // Groupes ouverts (mode déplié, accordéon) — la section contenant la page
  // courante s'ouvre automatiquement, sans jamais refermer les autres.
  const [openGroups, setOpenGroups] = React.useState({})
  React.useEffect(() => {
    const active = appMenu.find((group) =>
      (group.items || []).some((item) => item.to === currentPath),
    )
    if (active) {
      setOpenGroups((prev) => ({ ...prev, [active.titleKey]: true }))
    }
  }, [appMenu, currentPath])

  const toggleGroup = (key) =>
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }))

  // Mode replié : un clic sur l'icône d'un groupe ouvre un flyout listant
  // ses items (on ne perd aucune route, juste l'affichage change).
  const [flyout, setFlyout] = React.useState({ anchorEl: null, group: null })
  const openFlyout = (event, group) =>
    setFlyout({ anchorEl: event.currentTarget, group })
  const closeFlyout = () => setFlyout({ anchorEl: null, group: null })

  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED

  return (
    <Box
      component="nav"
      sx={{
        width,
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        alignSelf: 'flex-start',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.bg,
        borderRight: `1px solid ${colors.line}`,
        transition: 'width 0.2s ease',
        overflow: 'hidden',
        zIndex: 5,
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        <List dense disablePadding>
          {menu_main.map((item, idx) => (
            <SidebarStandaloneItem
              key={`main-${idx}`}
              item={item}
              collapsed={collapsed}
              selected={currentPath === item.to}
              label={t(item.titleKey)}
            />
          ))}
        </List>

        {appMenu.length > 0 && <Divider sx={{ borderColor: colors.line, my: 1 }} />}

        <List dense disablePadding>
          {appMenu.map((group) => {
            const Icon = group.icon
            const label = t(group.titleKey)
            const isOpen = !!openGroups[group.titleKey]
            const isGroupActive = (group.items || []).some(
              (item) => item.to === currentPath,
            )

            if (collapsed) {
              return (
                <Tooltip key={group.titleKey} title={label} placement="right">
                  <ListItemButton
                    onClick={(e) => openFlyout(e, group)}
                    sx={{
                      justifyContent: 'center',
                      mx: 1,
                      my: 0.4,
                      py: 1,
                      borderRadius: 1.5,
                      color: isGroupActive ? colors.accent : colors.text,
                      bgcolor: isGroupActive ? colors.accentBg : 'transparent',
                      '&:hover': { bgcolor: isGroupActive ? colors.accentBg : colors.hoverBg },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                      {Icon ? <Icon sx={{ fontSize: 22 }} /> : null}
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              )
            }

            return (
              <React.Fragment key={group.titleKey}>
                <ListItemButton
                  onClick={() => toggleGroup(group.titleKey)}
                  sx={{
                    mx: 1,
                    my: 0.2,
                    py: 0.9,
                    px: 1.25,
                    borderRadius: 1.5,
                    color: isGroupActive ? colors.accent : colors.text,
                    bgcolor: isGroupActive ? colors.accentBg : 'transparent',
                    '&:hover': { bgcolor: isGroupActive ? colors.accentBg : colors.hoverBg },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>
                    {Icon ? <Icon sx={{ fontSize: 20 }} /> : null}
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      fontWeight: isGroupActive ? 700 : 600,
                    }}
                  />
                  {isOpen ? (
                    <ExpandLessRoundedIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                  ) : (
                    <ExpandMoreRoundedIcon sx={{ fontSize: 18, opacity: 0.7 }} />
                  )}
                </ListItemButton>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List dense disablePadding>
                    {(group.items || []).map((item, idx) => {
                      const itemLabel = t(item.titleKey)
                      const selected = currentPath === item.to
                      return (
                        <ListItemButton
                          key={idx}
                          component={Link}
                          to={item.to}
                          selected={selected}
                          sx={{
                            ml: 3.5,
                            mr: 1,
                            my: 0.2,
                            py: 0.6,
                            px: 1.25,
                            borderRadius: 1.5,
                            color: selected ? colors.accent : colors.text2,
                            '&.Mui-selected': { bgcolor: colors.accentBg, color: colors.accent },
                            '&:hover': { bgcolor: colors.hoverBg },
                          }}
                        >
                          <ListItemText
                            primary={itemLabel}
                            primaryTypographyProps={{
                              fontSize: '0.8rem',
                              fontWeight: selected ? 700 : 400,
                            }}
                          />
                        </ListItemButton>
                      )
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            )
          })}
        </List>
      </Box>

      <Divider sx={{ borderColor: colors.line }} />
      <Tooltip title={t(collapsed ? 'nav.expandSidebar' : 'nav.collapseSidebar', {
        defaultValue: collapsed ? 'Déplier le menu' : 'Replier le menu',
      })} placement="right">
        <ListItemButton
          onClick={toggleCollapsed}
          sx={{
            justifyContent: collapsed ? 'center' : 'flex-start',
            py: 1.1,
            px: collapsed ? 0 : 1.75,
            color: colors.text2,
            '&:hover': { bgcolor: colors.hoverBg, color: colors.text },
          }}
        >
          {collapsed ? (
            <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
          ) : (
            <>
              <ChevronLeftRoundedIcon sx={{ fontSize: 20, mr: 1 }} />
              <ListItemText
                primary={t('nav.collapseSidebar', { defaultValue: 'Replier le menu' })}
                primaryTypographyProps={{ fontSize: '0.8rem' }}
              />
            </>
          )}
        </ListItemButton>
      </Tooltip>

      {/* Flyout — items d'un groupe quand la sidebar est repliée. */}
      <Menu
        anchorEl={flyout.anchorEl}
        open={Boolean(flyout.anchorEl)}
        onClose={closeFlyout}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            bgcolor: colors.paper,
            color: colors.text,
            border: `1px solid ${colors.line}`,
            minWidth: 220,
          },
        }}
      >
        {flyout.group && (
          <Box sx={{ px: 2, py: 1 }}>
            <Box sx={{ fontSize: '0.75rem', fontWeight: 700, color: colors.text2, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {t(flyout.group.titleKey)}
            </Box>
          </Box>
        )}
        {(flyout.group?.items || []).map((item, idx) => (
          <MenuItem
            key={idx}
            component={Link}
            to={item.to}
            onClick={closeFlyout}
            selected={currentPath === item.to}
            sx={{
              fontSize: '0.85rem',
              color: currentPath === item.to ? colors.accent : colors.text,
              '&.Mui-selected': { bgcolor: colors.accentBg },
              '&:hover': { bgcolor: colors.hoverBg },
            }}
          >
            {t(item.titleKey)}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

function SidebarStandaloneItem({ item, collapsed, selected, label }) {
  const Icon = item.icon
  const content = (
    <ListItemButton
      component={Link}
      to={item.to}
      selected={selected}
      sx={{
        justifyContent: collapsed ? 'center' : 'flex-start',
        mx: 1,
        my: 0.2,
        py: 1,
        px: collapsed ? 0 : 1.25,
        borderRadius: 1.5,
        color: selected ? colors.accent : colors.text,
        '&.Mui-selected': { bgcolor: colors.accentBg, color: colors.accent },
        '&:hover': { bgcolor: colors.hoverBg },
      }}
    >
      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 34, color: 'inherit' }}>
        {Icon ? <Icon sx={{ fontSize: 22 }} /> : null}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: selected ? 700 : 600 }}
        />
      )}
    </ListItemButton>
  )
  return collapsed ? (
    <Tooltip title={label} placement="right">
      {content}
    </Tooltip>
  ) : (
    content
  )
}
